import AircallWorkspaceImport from 'aircall-everywhere'
import { createContext, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { useAircallMe, useDialAircall, useLinkAircallIdentity } from '../../models/aircall'
import type { AircallIdentity, AircallStatus, RingingCall } from './types'

// CommonJS/ESM interop dance: the package's dist/index.js is a webpack
// bundle whose final `module.exports` is `{ default: AircallWorkspace }`.
// Depending on the bundler, the ESM default import here may resolve to
// that wrapper object instead of the inner class — and calling `new` on
// the wrapper throws "AircallWorkspace is not a constructor". Unwrap it.
type AircallWorkspaceCtor = typeof AircallWorkspaceImport
const AircallWorkspace = ((
  AircallWorkspaceImport as unknown as {
    default?: AircallWorkspaceCtor
  }
).default ?? AircallWorkspaceImport) as AircallWorkspaceCtor

const WIDGET_CONTAINER_ID = 'aircall-everywhere-iframe'

export interface AircallContextValue {
  status: AircallStatus
  error: Error | null
  isLoggedIn: boolean
  identity: AircallIdentity | null
  ringingCall: RingingCall | null
  expanded: boolean
  /** True when this staff user has an Aircall number configured for API SMS. */
  smsEnabled: boolean
  /** E.164 number SMS will send from, when smsEnabled. */
  smsFromNumber: string | null
  /** Where the widget iframe attaches. Render once in the AircallProvider tree. */
  widgetContainerId: string
  /** Workspace has been open a while without logging in — usually a third-party
   * cookie / cross-site block that leaves the embedded iframe blank. */
  workspaceStalled: boolean
  dial: (args: {
    to: string
    leadId?: string
    contactId?: string
    source?: 'lead-detail' | 'calls-page'
  }) => Promise<void>
  expand: () => void
  collapse: () => void
  disconnect: () => Promise<void>
  /** Open the Aircall workspace in a first-party popup so the user can log in —
   * establishes the session cookie the embedded (cross-site) iframe can't. */
  connect: () => void
}

const WORKSPACE_URL = 'https://workspace.aircall.io'

export const AircallContext = createContext<AircallContextValue | null>(null)

export function AircallProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AircallStatus>('loading')
  const [error, setError] = useState<Error | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [identity, setIdentity] = useState<AircallIdentity | null>(null)
  const [ringingCall, setRingingCall] = useState<RingingCall | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [workspaceStalled, setWorkspaceStalled] = useState(false)

  const sdkRef = useRef<InstanceType<AircallWorkspaceCtor> | null>(null)
  const mountedRef = useRef(false)

  const aircallMeQuery = useAircallMe()
  const linkIdentityMutation = useLinkAircallIdentity()
  const dialMutation = useDialAircall()

  // Single gate: the backend's global kill switch must be on before we load
  // the SDK. The SDK queries the DOM for `domToLoadWorkspace` at construction
  // time, so the container must exist BEFORE we instantiate. AircallWidget
  // renders a div with id=WIDGET_CONTAINER_ID at the root of the provider tree.
  useEffect(() => {
    if (mountedRef.current) return
    if (aircallMeQuery.isError) {
      mountedRef.current = true
      setError(aircallMeQuery.error as Error)
      setStatus('error')
      return
    }
    if (!aircallMeQuery.data) return
    mountedRef.current = true
    const me = aircallMeQuery.data
    if (!me.integrationEnabled) {
      setStatus('disabled')
      return
    }
    initSdk()

    function initSdk() {
      try {
        const sdk = new AircallWorkspace({
          domToLoadWorkspace: `#${WIDGET_CONTAINER_ID}`,
          size: 'big',
          debug: false,
          onLogin: async (payload) => {
            // The SDK's user payload shape isn't formally typed in their npm
            // package and the field names vary across Aircall workspaces.
            // Defensively try every common variant + log what arrived so we
            // can pin it down for real Aircall accounts.
            const u = (payload.user ?? {}) as Record<string, unknown>
            console.info('[aircall] onLogin payload', payload)
            const aircallUserId = Number(u.user_id ?? u.id ?? u.userId ?? NaN)
            const aircallEmail = String(u.user_email ?? u.email ?? u.userEmail ?? '')
            if (!aircallUserId || !aircallEmail) {
              console.warn(
                '[aircall] onLogin payload missing user id or email; skipping backend link',
                payload
              )
              setIsLoggedIn(true)
              return
            }
            setIdentity({ aircallUserId, aircallEmail })
            setIsLoggedIn(true)
            // Tell the backend who the staff member is, so webhooks can be
            // attributed. Verification happens server-side against the Aircall API.
            try {
              await linkIdentityMutation.mutateAsync({
                aircallUserId,
                email: aircallEmail,
              })
            } catch (err) {
              // Identity link failure shouldn't break the UI — the user can still
              // make and receive calls; webhooks just won't attribute to a CAI user.
              console.warn('aircall identity link failed', err)
            }
          },
          onLogout: () => {
            setIsLoggedIn(false)
            setIdentity(null)
          },
        })

        sdk.on('incoming_call', (call) => {
          const c = call as { from?: string; to?: string }
          setRingingCall({
            direction: 'inbound',
            from: c.from ?? '',
            to: c.to ?? '',
          })
          setExpanded(true)
        })
        sdk.on('outgoing_call', (call) => {
          const c = call as { from?: string; to?: string }
          setRingingCall({
            direction: 'outbound',
            from: c.from ?? '',
            to: c.to ?? '',
          })
          setExpanded(true)
        })
        sdk.on('call_ended', () => {
          setRingingCall(null)
          // Don't collapse — staff may want to take notes / see the in-call UI.
        })
        // An unanswered inbound call only fires `call_end_ringtone` (the
        // ringtone stopped) — `call_ended` fires only once a call has actually
        // connected. Clear the ringing banner on either so a missed call
        // doesn't leave "Incoming call …" stuck in the bar.
        sdk.on('call_end_ringtone', () => {
          setRingingCall(null)
        })

        sdkRef.current = sdk
        setStatus('ready')
      } catch (err) {
        setError(err as Error)
        setStatus('error')
      }
    }
  }, [aircallMeQuery.data, aircallMeQuery.isError, aircallMeQuery.error, linkIdentityMutation])

  const dial = useCallback(
    async ({
      to,
      leadId,
      contactId,
    }: {
      to: string
      leadId?: string
      contactId?: string
      source?: string
    }) => {
      if (!sdkRef.current) {
        throw new Error('Aircall SDK not ready')
      }
      // Backend hint first so the call.created webhook handler can pin the
      // resulting CallEvent to the right lead even if multiple share the number.
      if (leadId) {
        await dialMutation.mutateAsync({ to, leadId, contactId })
      }
      sdkRef.current.send('dial_number', { phone_number: to })
      setExpanded(true)
    },
    [dialMutation]
  )

  const expand = useCallback(() => setExpanded(true), [])
  const collapse = useCallback(() => setExpanded(false), [])
  const disconnect = useCallback(async () => {
    sdkRef.current?.send('logout')
  }, [])
  const connect = useCallback(() => {
    window.open(WORKSPACE_URL, 'aircall-login', 'popup,width=480,height=760')
  }, [])

  // If the workspace is open + ready but hasn't logged in after a few seconds,
  // the embedded (cross-site) iframe is almost certainly blocked — surface it so
  // the UI can offer the first-party `connect()` popup instead of a blank box.
  useEffect(() => {
    if (!expanded || status !== 'ready' || isLoggedIn) {
      setWorkspaceStalled(false)
      return
    }
    const timer = setTimeout(() => setWorkspaceStalled(true), 6000)
    return () => clearTimeout(timer)
  }, [expanded, status, isLoggedIn])

  const value: AircallContextValue = {
    status,
    error,
    isLoggedIn,
    identity,
    ringingCall,
    expanded,
    smsEnabled: aircallMeQuery.data?.smsEnabled ?? false,
    smsFromNumber: aircallMeQuery.data?.smsFromNumber ?? null,
    widgetContainerId: WIDGET_CONTAINER_ID,
    workspaceStalled,
    dial,
    expand,
    collapse,
    disconnect,
    connect,
  }

  return <AircallContext.Provider value={value}>{children}</AircallContext.Provider>
}
